<script setup lang="ts">
// Static illustration for the "Content" landing section, a slice of Ava's page
// with the real sections: links (icon style plus one card-style link), clips
// (ClipsDisplay, two side by side) and the audio player widget (AudioPlayer).

withDefaults(defineProps<{
  /** Drop the outer framed panel, for use inside a card that already frames it */
  bare?: boolean
}>(), { bare: false })

// null colour = page text colour (white brand icons vanish on the light theme)
const links = [
  { icon: 'i-simple-icons-youtube', color: '#FF0000' },
  { icon: 'i-simple-icons-tiktok', color: null },
  { icon: 'i-simple-icons-instagram', color: '#E4405F' },
  { icon: 'i-simple-icons-x', color: null },
  { icon: 'i-simple-icons-twitch', color: '#9146FF' }
]

const clips = [
  { photo: 'clip-sunset', title: 'sunset timelapse' },
  { photo: 'clip-city', title: 'city walk' }
]
</script>

<template>
  <div
    aria-hidden="true"
    class="flex w-full select-none flex-col pointer-events-none"
    :class="bare ? '' : 'rounded-2xl border border-default bg-elevated/30 p-5 sm:p-6 shadow-(--surface-shadow)'"
  >
    <MockPage
      bg-position="70% 40%"
      class="flex flex-1 flex-col justify-center rounded-xl p-4 font-space-grotesk sm:p-5"
    >
      <div class="mock-card mx-auto w-full max-w-sm px-5 pb-5 pt-4 [zoom:0.9]">
        <!-- Links -->
        <MockSectionTitle label="My links" />
        <div class="mt-4 flex flex-wrap items-center justify-center gap-2">
          <span
            v-for="link in links"
            :key="link.icon"
            class="flex size-10 items-center justify-center rounded-xl"
          >
            <UIcon
              :name="link.icon"
              class="size-5"
              :class="link.color ? '' : 'text-(--mock-text-strong)'"
              :style="link.color ? { color: link.color } : undefined"
            />
          </span>
        </div>
        <div class="mt-3 flex items-center gap-3 rounded-xl border border-(--mock-card-border) bg-(--mock-tile-bg) px-4 py-3">
          <UIcon
            name="i-simple-icons-kofi"
            class="size-5 shrink-0 text-[#FF5E5B]"
          />
          <span class="flex-1 text-left text-sm font-medium text-(--mock-text)">Support my work</span>
          <UIcon
            name="i-lucide-external-link"
            class="size-3.5 text-(--mock-text-dim)"
          />
        </div>

        <!-- Clips -->
        <div class="mt-6">
          <MockSectionTitle label="My clips" />
        </div>
        <div class="mt-4 grid grid-cols-2 gap-2">
          <div
            v-for="clip in clips"
            :key="clip.photo"
            class="relative aspect-video overflow-hidden rounded-lg border border-(--mock-card-border)"
          >
            <MockPhoto :name="clip.photo" />
            <div class="absolute inset-x-0 bottom-0 bg-linear-to-t from-black/70 to-transparent px-2 py-1">
              <p class="truncate text-xs text-white/90">
                {{ clip.title }}
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Audio player widget, as it sits in a page corner -->
      <div class="mt-4 w-72 max-w-full rounded-2xl border border-(--mock-glass-border) bg-(--mock-glass) shadow-[0_0_20px_rgba(0,0,0,0.12)] backdrop-blur-md">
        <div class="flex items-center gap-2.5 px-4 pb-1.5 pt-3">
          <div class="flex h-4 shrink-0 items-end gap-[3px] text-primary-400">
            <span
              v-for="i in 4"
              :key="i"
              class="wave-dot"
              :style="{ animationDelay: `${(i - 1) * 0.15}s` }"
            />
          </div>
          <p class="flex-1 truncate text-xs text-(--mock-text-muted)">
            night drive
          </p>
        </div>
        <div class="px-4 py-1">
          <div class="flex items-center gap-2">
            <span class="w-7 text-right text-[10px] tabular-nums text-(--mock-text-dim)">1:12</span>
            <div class="relative h-1 flex-1 rounded-full bg-(--mock-track)">
              <div class="absolute inset-y-0 left-0 w-2/5 rounded-full bg-primary-400/60" />
            </div>
            <span class="w-7 text-[10px] tabular-nums text-(--mock-text-dim)">3:04</span>
          </div>
        </div>
        <div class="flex items-center px-3 pb-3 pt-1">
          <span class="flex size-7 items-center justify-center text-(--mock-text-dim)">
            <UIcon
              name="i-lucide-skip-back"
              class="size-3.5"
            />
          </span>
          <span class="flex size-9 items-center justify-center rounded-full border border-primary-400/30 bg-primary-500/20 text-primary-500">
            <UIcon
              name="i-lucide-pause"
              class="size-4"
            />
          </span>
          <span class="flex size-7 items-center justify-center text-(--mock-text-dim)">
            <UIcon
              name="i-lucide-skip-forward"
              class="size-3.5"
            />
          </span>
          <div class="flex-1" />
          <div class="flex items-center gap-1.5">
            <UIcon
              name="i-lucide-volume-2"
              class="size-3 text-(--mock-text-dim)"
            />
            <div class="relative h-1 w-16 rounded-full bg-(--mock-track)">
              <div class="h-full w-2/3 rounded-full bg-primary-400/60" />
              <span class="absolute left-2/3 top-1/2 size-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white shadow" />
            </div>
          </div>
        </div>
      </div>
    </MockPage>
  </div>
</template>

<style scoped>
.wave-dot {
  width: 3px;
  height: 3px;
  border-radius: 9999px;
  background: currentcolor;
  animation: wave-bounce 1s ease-in-out infinite;
}

@keyframes wave-bounce {
  0%, 100% {
    transform: translateY(0);
  }

  50% {
    transform: translateY(-6px);
  }
}

@media (prefers-reduced-motion: reduce) {
  .wave-dot {
    animation: none;
  }
}
</style>
